import { Navigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { styled } from 'goober';
import { useEffect, useRef, useState, useMemo, useCallback } from 'react';

import { WithHtmlSavvyTitle } from '@/with-html-savvy-title';
import { useValidateShareLink } from '@/features/account/hooks/use-validate-share-link.query';
import { useAuthorization } from '@/auth/hooks/use-authorization';
import { Loader } from '@/components/loader/loader';
import { PageNotFound } from '@/routes/page-not-found';
import { RequestProjectUnauthenticatedPage } from './request-project-unauthenticated.page';
import { useAccountSwitchOnLinkValidation } from '@/features/account/hooks/use-account-switch-on-link-validation';
import { useCurrentUser } from '@/user/use-current-user.query';

const REDIRECT_PATH = '/request-project';

export const RequestProjectWithLinkWrapperPage = () => {
  const { linkId } = useParams<{ linkId: string }>();
  const { t } = useTranslation('request-project');
  const { isUserAuthorized } = useAuthorization();
  const currentUser = useCurrentUser();

  const {
    data: validationData,
    isLoading,
    error,
  } = useValidateShareLink(linkId || '');

  const [shouldRedirectAfterSwitch, setShouldRedirectAfterSwitch] =
    useState(false);
  const previousAccountNameRef = useRef<string>('');

  useEffect(() => {
    setShouldRedirectAfterSwitch(false);
    return () => {
      setShouldRedirectAfterSwitch(false);
    };
  }, [linkId]);

  const onSwitchComplete = useCallback((previousAccountName: string) => {
    previousAccountNameRef.current = previousAccountName;
    setShouldRedirectAfterSwitch(true);
  }, []);

  const { handleAccountSwitch, isSwitching, shouldSwitch } =
    useAccountSwitchOnLinkValidation({
      validationData,
      onSwitchComplete,
    });

  const getRedirectUrlWithNotification = useCallback(() => {
    if (!validationData) {
      return REDIRECT_PATH;
    }

    const url = new URL(REDIRECT_PATH, window.location.origin);
    url.searchParams.set('accountSwitched', 'true');
    url.searchParams.set('accountName', validationData.accountName || '');
    url.searchParams.set('previousAccountName', previousAccountNameRef.current);
    url.searchParams.set('entityType', 'request project');
    url.searchParams.set('fromLink', 'true');

    return url.pathname + url.search;
  }, [validationData]);

  useEffect(() => {
    if (shouldSwitch && !shouldRedirectAfterSwitch) {
      handleAccountSwitch();
    }
  }, [shouldSwitch, handleAccountSwitch, shouldRedirectAfterSwitch]);

  useEffect(() => {
    if (shouldRedirectAfterSwitch && !isSwitching) {
      const redirectUrl = getRedirectUrlWithNotification();
      if (redirectUrl) {
        window.location.href = redirectUrl;
      }
    }
  }, [shouldRedirectAfterSwitch, isSwitching, getRedirectUrlWithNotification]);

  const isLoadingState = isLoading || isSwitching;
  const isValidRedirect =
    validationData?.isValid &&
    validationData?.accountId &&
    currentUser.data &&
    currentUser.data.account.id === validationData.accountId;
  const shouldShowNotFound =
    !isLoading &&
    !shouldRedirectAfterSwitch &&
    (error ||
      !validationData?.isValid ||
      validationData?.purpose === 'SPOTLIGHT');

  const loadingComponent = useMemo(
    () => (
      <WithHtmlSavvyTitle title={t`title`}>
        <LoadingContainer>
          <Loader />
        </LoadingContainer>
      </WithHtmlSavvyTitle>
    ),
    [t],
  );

  if (isLoadingState) {
    return loadingComponent;
  }

  if (shouldShowNotFound) {
    return <PageNotFound />;
  }

  if ((isLoading || !validationData) && !shouldRedirectAfterSwitch) {
    return loadingComponent;
  }

  if (shouldRedirectAfterSwitch && !isSwitching) {
    return loadingComponent;
  }

  // If backend didn't return account details, link is invalid
  if (!validationData || !validationData.accountName) {
    return <PageNotFound />;
  }

  // If user is not authenticated, show unauthenticated page (public access)
  // Use linkId since accountId is not returned for unauthenticated users
  if (!isUserAuthorized) {
    if (!linkId) {
      return <PageNotFound />;
    }
    return (
      <RequestProjectUnauthenticatedPage
        organizationName={validationData.accountName}
        linkId={linkId}
        logoUrl={validationData.logoUrl}
      />
    );
  }

  // User is authenticated - handle account switching or redirect
  if (
    !shouldSwitch &&
    !isSwitching &&
    !shouldRedirectAfterSwitch &&
    isValidRedirect
  ) {
    return <Navigate to={REDIRECT_PATH} replace state={{ fromLink: true }} />;
  }

  // User is authenticated but account switching logic didn't run
  // For authenticated users, accountId should be present in validationData
  // Always pass linkId so form can use it for submission
  if (!validationData.accountId || !linkId) {
    return <PageNotFound />;
  }
  return (
    <RequestProjectUnauthenticatedPage
      organizationName={validationData.accountName}
      accountId={validationData.accountId}
      linkId={linkId}
      logoUrl={validationData.logoUrl}
    />
  );
};

const LoadingContainer = styled('div')`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
`;
